import { useEffect, useMemo, useState, Suspense } from 'react';
import {
  Title,
  Paper,
  Text,
  Container,
  Box,
  useMantineTheme,
  Stack,
} from '@mantine/core';
import { useLocation } from 'react-router-dom';
import classes from './PaymentPage.module.css';
import { FormContainer } from './FormContainer';
import type { TransactionResult } from './TransactionResult';
import { TransactionResultPanel } from './TransactionResult';
import { paymentPageReducer, initialState } from './paymentPageReducer';
import { LoadingView } from '@practist/components/LoadingView';
import { ErrorView } from '@practist/components/ErrorView';
import type { GetPaymentPageUnauthenticated } from '@/lib/cloudFunctions';
import LandingGradientBackground from '@practist/components/LandingGradientBackground/LandingGradientBackground';
import { extractVibrantColors } from '@practist/components/LandingGradientBackground/colorUtils';
import { useReducerWithSideEffects } from '@/util/reducerWithSideEffects';
import { NotFound } from '@practist/components/NotFound/NotFound';

const FALLBACK_GRADIENT_COLORS = {
  primary: '#ff00bf',
  secondary: '#ffe0f5',
  tertiary: '#ffaadd',
};

const COLOR_EXTRACTION_TIMEOUT_MS = 8000;

const PaymentPage = () => {
  const queryParams = useQuery();
  const paymentPageId = queryParams.get('id');
  const theme = useMantineTheme();

  const [state, dispatch] = useReducerWithSideEffects(
    paymentPageReducer,
    initialState,
  );
  const [gradientColors, setGradientColors] = useState<
    | {
        primary: string;
        secondary: string;
        tertiary?: string;
      }
    | undefined
  >();
  const [colorExtractionError, setColorExtractionError] = useState<
    string | null
  >(null);

  useEffect(() => {
    dispatch({
      type: 'PAGE_ID_CHANGED',
      pageId: paymentPageId ?? undefined,
    });
  }, [dispatch, paymentPageId]);

  useEffect(() => {
    if (state.name === 'PAGE_INFO_LOADED') {
      if (state.info.firmLogoUrl) {
        extractColorsFromLogo(state.info.firmLogoUrl);
      } else {
        setGradientColors(FALLBACK_GRADIENT_COLORS);
      }
    } else if (state.name === 'PAGE_INFO_LOAD_ERROR') {
      // Without this fallback the page is stuck on the loader: the gradient
      // gate below requires gradientColors !== undefined, but the color
      // extraction effect only runs in the loaded state, so the error view
      // would never get a chance to render.
      setGradientColors(FALLBACK_GRADIENT_COLORS);
    }
  }, [state]);

  const extractColorsFromLogo = async (logoUrl: string) => {
    setColorExtractionError(null);

    try {
      // Promise.race guarantees we set gradientColors within the timeout
      // even if extractVibrantColors hangs — an iOS Safari edge case where
      // an <img> marks itself complete without firing onload/onerror leaves
      // the inner Promise unresolved, which would otherwise leave the page
      // stuck on the loader forever.
      const vibrantColors = await Promise.race([
        extractVibrantColors(logoUrl),
        new Promise<typeof FALLBACK_GRADIENT_COLORS>((resolve) => {
          setTimeout(() => {
            resolve(FALLBACK_GRADIENT_COLORS);
          }, COLOR_EXTRACTION_TIMEOUT_MS);
        }),
      ]);
      setGradientColors(vibrantColors);
    } catch (error) {
      console.error('Failed to extract colors from logo:', error);
      setColorExtractionError(
        error instanceof Error ? error.message : 'Unknown error',
      );
      setGradientColors(FALLBACK_GRADIENT_COLORS);
    }
  };

  if (state.name === 'PAGE_EXPIRED' || state.name === 'PAGE_ID_MISSING') {
    return (
      <Suspense
        fallback={<LoadingView includeTitle={false} includeAnimation={true} />}
      >
        <NotFound />
      </Suspense>
    );
  }

  if (gradientColors === undefined) {
    return <LoadingView includeTitle={false} includeAnimation={true} />;
  }

  return (
    <LandingGradientBackground
      primaryColor={gradientColors.primary}
      secondaryColor={gradientColors.secondary}
      tertiaryColor={gradientColors.tertiary}
    >
      {process.env.NODE_ENV === 'development' && (
        <>
          {colorExtractionError && (
            <Box
              style={{
                background: 'rgba(255, 0, 0, 0.1)',
                padding: theme.spacing.md,
                margin: theme.spacing.md,
                borderRadius: theme.radius.md,
                border: '1px solid red',
              }}
            >
              <Text>Color extraction error: {colorExtractionError}</Text>
              <Text>Using fallback colors instead.</Text>
              <Text fz='xs' fs='italic' style={{ fontStyle: 'italic' }}>
                (This message is only visible in development mode)
              </Text>
            </Box>
          )}
        </>
      )}
      <Container size='lg' className={classes.paymentPageContainer}>
        <Paper
          className={classes.paymentPaper}
          shadow='sm'
          radius='lg'
          p={0}
          withBorder
          style={
            {
              '--primary-color': gradientColors.primary,
              '--secondary-color': gradientColors.secondary,
              '--tertiary-color':
                gradientColors.tertiary || gradientColors.primary,
            } as React.CSSProperties
          }
        >
          {state.name === 'PAGE_INFO_LOADED' ? (
            <PaymentPageInfo
              paymentPageId={paymentPageId ?? ''}
              paymentPage={state.info}
              colorPalette={gradientColors}
            />
          ) : state.name === 'PAGE_INFO_LOADING' ? (
            <LoadingView />
          ) : state.name === 'PAGE_INFO_LOAD_ERROR' ? (
            <ErrorView />
          ) : null}
        </Paper>
      </Container>
    </LandingGradientBackground>
  );
};

const PaymentPageInfo = ({
  paymentPageId,
  paymentPage,
  colorPalette,
}: {
  paymentPageId: string;
  paymentPage: GetPaymentPageUnauthenticated.SuccessPayload;
  colorPalette: {
    primary: string;
    secondary: string;
    tertiary?: string;
  };
}) => {
  const [transactionResultData, setTransactionResultData] =
    useState<TransactionResult>();

  return (
    <Stack className={classes.paymentPageInfo}>
      <Header firmLogoUrl={paymentPage.firmLogoUrl} />

      <Box className={classes.paymentStagesContainer}>
        {transactionResultData === undefined && (
          <FormContainer
            firmId={paymentPage.firmId}
            matterId={paymentPage.matterId}
            firmName={paymentPage.firmName}
            finixMerchantId={paymentPage.finixMerchantId}
            initialTransactionAmount={paymentPage.transactionAmount}
            opacity={transactionResultData !== undefined ? 0 : 1}
            onTransactionEnded={setTransactionResultData}
            paymentPageId={paymentPageId}
            colorPalette={colorPalette}
            availablePaymentMethods={paymentPage.availablePaymentMethods}
            matterName={paymentPage.matterName}
            attorneyName={paymentPage.attorneyName}
            practitionerTitleLabel={paymentPage.practitionerTitleLabel}
            accountType={paymentPage.accountType}
            suggestedAmount={paymentPage.suggestedAmount}
          />
        )}
        {transactionResultData !== undefined && (
          <TransactionResultPanel
            opacity={transactionResultData !== undefined ? 1 : 0}
            summary={transactionResultData}
          />
        )}
      </Box>
    </Stack>
  );
};

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

export default PaymentPage;

const Header = ({ firmLogoUrl }: { firmLogoUrl: string }) => {
  return (
    <Box className={classes.pageHeader}>
      <Box className={classes.logoContainer}>
        <img src={firmLogoUrl} alt='Firm logo' className={classes.firmLogo} />
      </Box>
      <Box className={classes.titleContainer}>
        <Stack gap='xs' align='center'>
          <Title order={1} fw={600} className={classes.pageTitle}>
            Make a Payment
          </Title>
        </Stack>
      </Box>
    </Box>
  );
};
